import { Space, Typography, Switch, Card } from 'antd';
import React from 'react';
import { Helmet } from 'react-helmet';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../helpers/use-store';
import { ConfigurableEmailType } from '@monorepo/types';
import { LoadingState, useLoading } from '@monorepo/react-components';
import { useNotification } from '../../../hooks/use-notification';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

const EmailNotifications = observer(() => {
  const { t } = useTranslation();
  const {
    dataStore: { userStore },
  } = useStore();
  const { loadingState, updateLoadingState } = useLoading();
  const notificationApi = useNotification();

  const EMAIL_NOTIFICATION_CONFIG = {
    [ConfigurableEmailType.VideoRendered]: {
      label: t('settings.notifications.videoReady'),
      description: t('settings.notifications.videoReadyDescription'),
    },
    [ConfigurableEmailType.SeriesDisabled]: {
      label: t('settings.notifications.weeklyReport'),
      description: t('settings.notifications.weeklyReportDescription'),
    },
    [ConfigurableEmailType.LowCredits]: {
      label: t('settings.notifications.marketing'),
      description: t('settings.notifications.marketingDescription'),
    },
    [ConfigurableEmailType.NoCredits]: {
      label: t('settings.notifications.marketing'),
      description: t('settings.notifications.marketingDescription'),
    },
    [ConfigurableEmailType.SocialAccountReconnect]: {
      label: t('settings.notifications.marketing'),
      description: t('settings.notifications.marketingDescription'),
    },
  };

  const emailNotificationSettings = Object.entries(
    EMAIL_NOTIFICATION_CONFIG
  ).map(([type, config]) => ({
    type: type as ConfigurableEmailType,
    ...config,
  }));

  const isNotificationEnabled = (type: ConfigurableEmailType): boolean => {
    return userStore.emailNotifications?.[type] !== false;
  };

  const handleToggle = async (
    type: ConfigurableEmailType,
    checked: boolean
  ) => {
    if (!userStore.currentUser) return;

    updateLoadingState(LoadingState.Loading);
    try {
      await userStore.updateEmailNotifications({
        ...userStore.emailNotifications,
        [type]: checked,
      });

      notificationApi.success({
        placement: 'top',
        message: t('common.success'),
        description: t('settings.notifications.saveSuccess'),
      });
    } catch (error) {
      notificationApi.error({
        placement: 'top',
        icon: '',
        message: t('common.error'),
        description: t('settings.notifications.saveError'),
      });
      console.error('Error updating email notifications:', error);
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ display: 'flex' }}>
      <Helmet>
        <title>Email Notifications | ShortsNinja</title>
        <meta
          name="description"
          content="Manage your email notification preferences for ShortsNinja."
        />
        <meta
          name="keywords"
          content="email notifications, notification settings, ShortsNinja"
        />
      </Helmet>
      <div>
        <Title level={4}>{t('settings.notifications.title')}</Title>
        <Space
          direction="vertical"
          size="middle"
          style={{ display: 'flex', marginTop: '16px' }}
        >
          {emailNotificationSettings.map((setting) => (
            <Card key={setting.type}>
              <Space
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <Text strong>{setting.label}</Text>
                  <br />
                  <Text type="secondary">{setting.description}</Text>
                </div>
                <Switch
                  checked={isNotificationEnabled(setting.type)}
                  onChange={(checked) => handleToggle(setting.type, checked)}
                  loading={loadingState === LoadingState.Loading}
                />
              </Space>
            </Card>
          ))}
        </Space>
      </div>
    </Space>
  );
});

export default EmailNotifications;
